import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const BunbuniPass = () => {
  const tourData = {
    name: "Bunbuni Pass Trek",
    duration: "03 Nights 04 Days",
    price: "₹ 4,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935854/vejvflj60hqcacq9etey.jpg",
    altitude: "9,700 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["Mountain pass", "Forest trails", "Scenic views"],
    bestTime: "April to November",
    description: "The Bunbuni Pass Trek is a beautiful moderate trek in Himachal Pradesh that takes you through scenic mountain passes and forest trails. This trek offers breathtaking views of the surrounding peaks and valleys, making it a perfect choice for adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Bunbuni Pass Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Bunbuni Pass Trek in Himachal Pradesh. Mountain pass trek with forest trails and scenic views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Mountain pass trek with forest trails and scenic views
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you through the beautiful Bunbuni Pass, offering stunning 
                    views of the surrounding mountains and valleys. The journey passes through 
                    dense forests, lush meadows, and challenging terrain, making it an exciting 
                    adventure for trekkers of all levels.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Mountain pass adventure</li>
                    <li>Forest trails and lush meadows</li>
                    <li>Scenic mountain views</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Photography opportunities</li>
                    <li>High-altitude experience</li>
                    <li>Challenging terrain</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Delhi to Kasol</h4>
                    <ul>
                      <li>In an air-conditioned vehicle, we leave Delhi at around ten o'clock at night.</li>
                      <li>Recognise the genuine worth of time, don't waste it, and make the most of each and every minute of it.</li>
                      <li>A pit stop for dinner at any nice restaurant by the side of the road.</li>
                      <li>On the road to Kasol, the Delhi Manali Highway is a great place to snap photographs and enjoy scenery.</li>
                      <li>We will reach Kasol early morning, you can rest in your stays and freshup for today's adventure.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Explore Kasol and Visit Manikaran</h4>
                    <ul>
                      <li>When you wake up, you will be greeted with a panoramic vista of this lovely town.</li>
                      <li>After you have finished your breakfast in the midst of nature, you will next go to the Manikaran Gurudwara sahib, which is located about 5 km away from Kasol.</li>
                      <li>A revitalising soak in the hot springs is available for your enjoyment here.</li>
                      <li>Spend the rest of the day relaxing at the camp site, browsing around the local market, or sitting by the riverside.</li>
                      <li>Enjoy the evening with music and a campfire (this may vary depending on the camp location and the provider of your stay).</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Kasol to Bunbuni</h4>
                    <ul>
                      <li>After breakfast, embark on a trek from Kasol to Bunbuni Pass Base Camp.</li>
                      <li>The trek route passes through picturesque landscapes, dense forests, and lush meadows.</li>
                      <li>Enjoy breathtaking views of the surrounding mountains and valleys along the way.</li>
                      <li>Reach Bunbuni Pass Base Camp by late afternoon or early evening.</li>
                      <li>Set up camp for the night and relax amidst the tranquility of the Himalayas.</li>
                      <li>Dinner and overnight stay at the base camp.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Bunbuni to Kasol to Delhi</h4>
                    <ul>
                      <li>Wake up early to start your ascent towards Bunbuni Pass, which stands at an altitude of approximately 4,200 meters.</li>
                      <li>The trek to Bunbuni Pass involves a steep and challenging climb, so pace yourself accordingly and take breaks as needed.</li>
                      <li>Reach Bunbuni Pass by late morning or midday and soak in the mesmerizing panoramic views of the surrounding peaks and valleys.</li>
                      <li>Spend some time at the pass, capturing photographs and enjoying the serenity of the high-altitude landscape.</li>
                      <li>Begin your descent back to Kasol after exploring Bunbuni Pass and then get ready for long journey back to Delhi.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Delhi</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during pick and drop transfers</li>
                    <li>Porters/mules charges</li>
                    <li>Personal expenses</li>
                    <li>Insurance</li>
                    <li>Anything apart from the inclusions</li>
                    <li>GST 5%</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>9,700 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>16 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>April to November</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Delhi)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 5,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 5,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate between 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Mountain pass adventure</li>
                    <li>Forest trails and lush meadows</li>
                    <li>Scenic mountain views</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Photography opportunities</li>
                    <li>High-altitude experience</li>
                    <li>Challenging terrain</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default BunbuniPass;
